import { ReactNode } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

import { LoanIcon } from "components/icons";

import { InventoryPillMeta } from "./inventory-product-field.util";

const SERIAL_BG = "#FAEEDA";
const SERIAL_TEXT = "#633806";
const CONDITION_BG = "#E1F5EE";
const CONDITION_TEXT = "#0F6E56";
// Red tint matches the loaner marker used across inventory (the red LoanIcon in
// the inventory list's ConditionIndicator), SN-19424.
const LOANER_BG = "#FDECEA";
const LOANER_TEXT = "#B42318";

const pillBaseSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "11.5px",
  lineHeight: 1,
  px: "8px",
  py: "4px",
  borderRadius: "8px",
  whiteSpace: "nowrap"
};

function Pill({
  icon,
  children,
  sx
}: {
  icon?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      component="span"
      sx={
        [pillBaseSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>
      }
    >
      {icon}
      <Box component="span">{children}</Box>
    </Box>
  );
}

export default function InventoryProductPills({
  meta
}: {
  meta: InventoryPillMeta;
}) {
  const { serial, condition, color, loanable, clinic, others } = meta;

  const hasMeta =
    Boolean(serial) ||
    Boolean(condition) ||
    Boolean(color) ||
    Boolean(loanable) ||
    Boolean(clinic) ||
    others.length > 0;

  if (!hasMeta) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        mt: "6px"
      }}
    >
      {Boolean(loanable) && (
        <Pill
          icon={<LoanIcon style={{ width: 13, height: 13 }} />}
          sx={{
            backgroundColor: LOANER_BG,
            color: LOANER_TEXT,
            fontWeight: 600
          }}
        >
          Loaner
        </Pill>
      )}

      {Boolean(serial) && (
        <Pill
          icon={
            <Box
              component="span"
              sx={{ fontSize: "13px", fontWeight: 600, lineHeight: 1 }}
            >
              #
            </Box>
          }
          sx={{
            backgroundColor: SERIAL_BG,
            color: SERIAL_TEXT,
            fontFamily: "monospace",
            fontWeight: 500
          }}
        >
          {serial}
        </Pill>
      )}

      {Boolean(condition) && (
        <Pill
          icon={<CheckCircleIcon sx={{ fontSize: "13px" }} />}
          sx={{
            backgroundColor: CONDITION_BG,
            color: CONDITION_TEXT
          }}
        >
          {condition}
        </Pill>
      )}

      {Boolean(color) && (
        <Pill
          icon={
            <Box
              component="span"
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: color,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                flexShrink: 0
              }}
            />
          }
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.grey[100],
            color: "text.secondary"
          }}
        >
          {color}
        </Pill>
      )}

      {Boolean(clinic) && (
        <Pill
          icon={<PlaceOutlinedIcon sx={{ fontSize: "13px" }} />}
          sx={
            clinic?.isCurrent
              ? {
                  backgroundColor: CONDITION_BG,
                  color: CONDITION_TEXT,
                  fontWeight: 600
                }
              : {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[800]
                      : theme.palette.grey[100],
                  color: "text.secondary"
                }
          }
        >
          {clinic?.name}
        </Pill>
      )}

      {others.map((text) => (
        <Pill
          key={text}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.grey[100],
            color: "text.secondary"
          }}
        >
          {text}
        </Pill>
      ))}
    </Box>
  );
}
